import clsx from "clsx";
import NotificationZone from "components/NotificationZone/NotificationZone";
import Zoom from "components/Zoom/Zoom";
import { NOTIFICATION_EVENT, STATUS_CHANGED_EVENT, WEBSOCKET_CONNECTED_EVENT, WEBSOCKET_FAILED_EVENT } from "constants/events";
import { EMPLACEMENT_TYPE, INVALID_INDEX, PARK_TYPE } from "constants/globals";
import { TestIds } from "constants/Tests/TestIds";
import {
  WAREHOUSE_FIELD_FILTER,
  WEB_SOCKET_URL,
} from "constants/webhooksConstants";
import { useAppDispatch, useAppSelector } from "hooks/StoreHooks";
import { useGridMathScroller } from "hooks/useGridScroller";
import { transformNotificationResponseToResource } from "models/Responses/NotificationResponse";
import {
  transformStatusResponseElementToResource
} from "models/Responses/StatusResponse";
import React, {
  cloneElement,
  ForwardRefExoticComponent,
  ReactElement,
  RefObject,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import StompComponent, {
  StompFilter,
} from "services/webSockets/StompComponent";
import { SubscriptionInfos } from "services/webSockets/SubscriptionSocket";
import { selectToken } from "store/Selectors/appStateSelector";
import {
  getGridLayoutHeight,
  getGridLayoutWidth,
  getZoomLevel,
} from "store/Selectors/gridLayoutSelector";
import { getURLParamDefaultPosition } from "store/Selectors/urlInfoSelector";
import {
  getOpenedDetailId,
  setOpenedDetailId,
  updateNotification,
} from "store/Slices/gridEventSlice";
import { updateGridElement } from "store/Slices/Status/statusSlice";
import Legend from "../Legend/Legend";

import { createErrorSnackBar } from "components/Errors/ErrorComponent";
import ScrollContainer from "react-indiana-drag-scroll";
import "./WarehouseGrid.scss";

export const DEFAULT_GRID_COLUMN_AND_ROW_SIZE = 2.2;
export const WAREHOUSE_GRID_ID = "warehouse-grid";
export const WAREHOUSE_PLAN_ID = "warehouse-plan";
const SUBBED_EVENTS_LIST = [STATUS_CHANGED_EVENT, NOTIFICATION_EVENT];

type RefForwardableElement = ReactElement<
  any,
  string | ForwardRefExoticComponent<any>
>;

type Props = {
  gridElements: RefForwardableElement[];
  shortcuts: RefForwardableElement[];
  storeNb?: number;
};

const WarehouseGrid: React.FC<Props> = ({
  gridElements,
  shortcuts,
  storeNb,
}) => {
  const gridHeight = useAppSelector(getGridLayoutHeight);
  const gridWidth = useAppSelector(getGridLayoutWidth);
  const zoomLevel = useAppSelector(getZoomLevel);
  const urlGridCellPosition = useAppSelector(getURLParamDefaultPosition);
  const authToken = useAppSelector(selectToken);
  const gridRef = useRef<HTMLDivElement>(null);
  const { scrollToCell } = useGridMathScroller(gridRef);
  const [scrollTarget, setScrollTarget] = useState<{
    row: number;
    col: number;
  } | null>(null);

  const dispatch = useAppDispatch();
  const openedDetailId = getOpenedDetailId;
  const [, setWindowPosition] = useState<{
    top: number;
    left: number;
  } | null>();

  const elementRefs = useRef<RefObject<HTMLDivElement | null>[]>(
    gridElements.map(() => React.createRef<HTMLDivElement>())
  );

  const warehouseWebSocketFilter = useMemo(() => {
    if (storeNb == undefined) return;
    else {
      return {
        field: WAREHOUSE_FIELD_FILTER,
        operator: "==",
        value: storeNb ? storeNb.toString() : "0",
      } as StompFilter;
    }
  }, [storeNb]);

  const subbedEventsAndFilters = useMemo<SubscriptionInfos[]>(
    () => [
      ...SUBBED_EVENTS_LIST.map(event => ({
        subbedEvent: event,
        filters: warehouseWebSocketFilter ? [warehouseWebSocketFilter] : [],
      })),
    ],
    [warehouseWebSocketFilter]
  );

  const [webSocketError, setWebSocketError] = useState<string | null>(null);

  useEffect(() => {
    if (
      urlGridCellPosition.start.x != 0 ||
      (urlGridCellPosition.start.y != 0 && gridRef)
    ) {
      setScrollTarget({
        row: urlGridCellPosition.start.x,
        col: urlGridCellPosition.start.y,
      });
    }
  }, [urlGridCellPosition, gridRef]);

  useEffect(() => {
    if (!scrollTarget) return;

    requestAnimationFrame(() => {
      scrollToCell(scrollTarget.row, scrollTarget.col, {
        behavior: "smooth",
        align: "center",
        cellHeightEm: DEFAULT_GRID_COLUMN_AND_ROW_SIZE,
        cellWidthEm: DEFAULT_GRID_COLUMN_AND_ROW_SIZE,
      });
    });
  }, [scrollTarget, scrollToCell]);

  function handleApplicatEvent(eventMsg: any) {
    if (eventMsg.event?.type === WEBSOCKET_CONNECTED_EVENT) {
      setWebSocketError(null);
    }
    if (eventMsg.event?.type === WEBSOCKET_FAILED_EVENT) {
      setWebSocketError(`La connexion pour la mise-à-jour en temps réelle a échoué (${eventMsg.data.code}).`);
      console.log(eventMsg.data);
    }
    if (eventMsg.event?.type === STATUS_CHANGED_EVENT) {
      const statusData = transformStatusResponseElementToResource(eventMsg.data);
      dispatch(updateGridElement(statusData));
    }
    if (eventMsg.event?.type === NOTIFICATION_EVENT) {
      const notificationResource = transformNotificationResponseToResource(eventMsg.data)
      dispatch(updateNotification(notificationResource));
    }
  }

  useEffect(() => {
    if (!openedDetailId) {
      setWindowPosition(null);
      return;
    }

    const elementIndex = gridElements.findIndex(
      el => el.props.id === openedDetailId
    );
    if (elementIndex === -1) return;

    const elementNode = elementRefs.current[elementIndex]?.current;
    if (!elementNode) return;

    const rect = elementNode.getBoundingClientRect();
    const windowWidth = 390;
    const spaceOnRight = window.innerWidth - rect.right;

    const left =
      spaceOnRight < windowWidth && rect.left > windowWidth
        ? rect.left - windowWidth + 40
        : rect.right + 16;

    setWindowPosition({
      top: rect.top + window.scrollY,
      left: left,
    });
  }, [openedDetailId, gridElements]);

  const handleClose = () => {
    dispatch(setOpenedDetailId(null));
  };

  const currentGridColumnAndRowSize = useMemo(() => {
    return zoomLevel
      ? (zoomLevel / 100) * DEFAULT_GRID_COLUMN_AND_ROW_SIZE
      : DEFAULT_GRID_COLUMN_AND_ROW_SIZE;
  }, [zoomLevel]);

  if (
    (!gridElements || gridElements.length === 0,
    subbedEventsAndFilters.length < 1)
  ) {
    return <div>Pas de données</div>;
  }

  const BORDER_TRACKS = 1;
  const outerCols = gridWidth + BORDER_TRACKS * 2;
  const outerRows = gridHeight + BORDER_TRACKS * 2;

  const cellSizeEm = `${currentGridColumnAndRowSize}em`;

  const outerTemplateCols = `repeat(${outerCols}, ${cellSizeEm})`;
  const outerTemplateRows = `repeat(${outerRows}, ${cellSizeEm})`;

  return (
    <StompComponent
      webSocketUrl={WEB_SOCKET_URL}
      authToken={authToken}
      subscriptions={subbedEventsAndFilters}
      handleApplicatEvent={handleApplicatEvent}>
      <>
      </>
      <div
        id={WAREHOUSE_PLAN_ID}
        className={clsx(`warehouse-${storeNb}`, `zoom-${zoomLevel}`)}
        onClick={handleClose}>
        {storeNb != INVALID_INDEX ? <NotificationZone /> : null}
        {webSocketError && createErrorSnackBar(webSocketError)}
        <ScrollContainer 
        hideScrollbars={false}
        >
            <div
               id={WAREHOUSE_GRID_ID}
              className="inner-grid"
              data-testid={TestIds.GRID}
              style={{
                gridColumn: `${BORDER_TRACKS} / ${outerCols}`,
                gridRow: `${BORDER_TRACKS} / ${outerRows}`,
                display: "grid",
                gridTemplateColumns: `repeat(${gridWidth}, ${cellSizeEm})`,
                gridTemplateRows: `repeat(${gridHeight}, ${cellSizeEm})`,
                position: "relative"
              }}>
              {gridElements.map((element, index) =>
                (React.isValidElement(element) &&
                  element.type == EMPLACEMENT_TYPE) ||
                PARK_TYPE
                  ? cloneElement(element, {
                      ref: elementRefs.current[index],
                    })
                  : element
              )}
              {shortcuts}
              <Zoom />
              <Legend key="legend" />
            </div>
        </ScrollContainer>
      </div>
    </StompComponent>
  );
};

export default WarehouseGrid;
